import type { MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useHome2Enabled } from '../../../../lib/home2-flag';
import { useChatsGate } from '../../../../lib/chats-flag';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useOnboardingEntry } from '../../../../lib/use-onboarding-entry';
import { useTranslation } from '../../../../lib/i18n';
import { useNeedsPhoneConnect, useOpenPhoneConnect } from '../../../../lib/phone-connect';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { VERIFY_CONNECT } from '../../PhoneVerifyModal';

// The rail carries destinations you can jump to from any page: Chats,
// Home (the `/` homepage grid), and Your agents.
// Everything else lives under those or under a workspace (the Symphony IA,
// matching the native drawer).
//
// Connectors used to be a destination here and is now a Settings row: "what am I
// hooked up to?" is something you check occasionally, not a place you work
// (docs/connectors-page-design.md §1). Its routes all still resolve.
// 'contacts' is deliberately NOT here: the destination moved onto the Your Agents hub, beside
// Tasks / Routines / Files / Apps, because it answers the same question those do. The route still
// exists and is still deep-linkable — only the rail entry is gone.
export type NavId = 'chats' | 'feed' | 'agents' | 'connect-whatsapp';

export interface SidebarNavItem {
  id: NavId;
  label: string;
  /** The Next page file, for `<Link href>`. */
  route: string;
  /** The URL the browser shows, for `<Link as>` — mount-prefixed. */
  path: string;
  isActive: boolean;
  onClick: (e: MouseEvent) => void;
}

interface SidebarNav {
  items: SidebarNavItem[];
  /** An action beside Chats, never a second primary destination. */
  newChat: { label: string; visible: boolean; disabled: boolean; onClick: (e: MouseEvent) => void };
}

// The NAVIGATING destinations that report a click. `connect-whatsapp` reports its own, on the branch
// that opens the dialog in place — it never reaches this.
const MENU_BI_NAV_IDS = new Set<NavId>(['chats', 'agents']);

const DESTINATIONS: { id: NavId; labelKey: string; route: string }[] = [
  { id: 'chats', labelKey: 'chats.title', route: '/chats' },
  { id: 'feed', labelKey: 'nav.catchUp', route: '/' },
  { id: 'agents', labelKey: 'nav.yourAgents', route: '/agents' },
  // Conditional — offered only while there is no number on file (see `offerConnect`). It is a
  // destination rather than a card because the number is what makes WhatsApp, SMS and every
  // away-from-the-app notification work, so it stays reachable from every page until it is done.
  { id: 'connect-whatsapp', labelKey: 'nav.connectWhatsapp', route: `/?verify=${VERIFY_CONNECT}` },
];

// Which destinations the rail offers, what they're called, where they go, and what
// clicking one does. New Chat is a separate action beside the Chats destination.
// Shared by the wide rail and the mobile drawer so both behave identically and only their markup differs; each UI
// pairs an item with its glyph from ../components/nav-icons.
export function useSidebarNav({
  current,
  onClose,
}: {
  current?: NavId;
  /** Narrow drawer: close it when a destination is picked. */
  onClose?: () => void;
} = {}): SidebarNav {
  const basePath = useBasePath();
  const router = useRouter();
  const { t } = useTranslation();
  const { openMaestroChat, setActiveConversationId, setChatMode } = useActiveConversation();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const { route: onboardingRoute, resumeConversationId, resolved: onboardingResolved } = useOnboardingEntry();
  // The rail asks for a number only when there is nothing on file to use — the same question the
  // Connectors page's WhatsApp actions ask, so it is answered in one place for all of them.
  const offerConnect = useNeedsPhoneConnect();
  const openPhoneConnect = useOpenPhoneConnect();
  // The map home replaced Your Agents (its ring IS the team, landed on from /agents), so the rail stops
  // offering the redundant destination while the arm is on. The route itself stays for deeplinks.
  const home2 = useHome2Enabled();
  const { enabled: chatsEnabled } = useChatsGate();

  const onNewChat = (e: MouseEvent) => {
    e.preventDefault();
    if (!onboardingResolved) return;
    onClose?.();
    logPageClickBi({ page_name: 'menu', button_name: 'new_chat', origin: 'menu' });
    // Until onboarding is finished, New Chat resumes it instead of orphaning setup.
    if (onboardingRoute === 'resume' && resumeConversationId) {
      setActiveConversationId(resumeConversationId);
      setChatMode('fullscreen');
    } else if (onboardingRoute === 'kickoff') {
      // Open fullscreen so the derived kickoff greets; a fresh `?new=1` thread would block it.
      setChatMode('fullscreen');
      void router.push('/', `${basePath || '/'}`);
    } else {
      openMaestroChat();
    }
    if (chatsEnabled && router.pathname === '/chats' && onboardingRoute !== 'kickoff') {
      const query = {
        ...(typeof router.query.q === 'string' ? { q: router.query.q } : {}),
        ...(typeof router.query.channel === 'string' ? { channel: router.query.channel } : {}),
        ...(onboardingRoute === 'resume' && resumeConversationId ? { conversation: resumeConversationId } : { new: '1' }),
      };
      void router.push({ pathname: '/chats', query }, `${basePath}/chats?${new URLSearchParams(query)}`, { shallow: true, scroll: false });
    }
  };

  const onClick = (id: NavId) => (e: MouseEvent) => {
    if (id === 'chats' && !chatsEnabled) {
      onNewChat(e);
      return;
    }
    // The verification dialog is not a place — it is a step over wherever the caller already is, so it
    // opens in place and the page behind it never unmounts. Its `route` stays a real href so a
    // cmd/middle-click still lands somewhere sensible.
    //
    // It used to navigate here, because the dialog was mounted on the home alone: answering the ask
    // meant throwing away whatever the caller was doing, and on an unfinished onboarding the home then
    // resumed the setup chat fullscreen on arrival — so a click from Connectors landed on
    // `/?verify=connect&chat=fullscreen`. The mount moved to AppFrame; this is the other half of it.
    if (id === 'connect-whatsapp') {
      e.preventDefault();
      onClose?.();
      logPageClickBi({ page_name: 'menu', button_name: id, origin: 'menu' });
      openPhoneConnect();
      return;
    }
    // A real navigation from the rail/drawer — mark the entry it lands on as drawer-opened so its
    // header shows the menu, not back (native's `openedFromDrawer`).
    markNavFromDrawer();
    leaveChat();
    onClose?.();
    if (!MENU_BI_NAV_IDS.has(id)) return;
    logPageClickBi({ page_name: 'menu', button_name: id, origin: 'menu' });
  };

  return {
    items: DESTINATIONS.filter(({ id }) => (id !== 'connect-whatsapp' || offerConnect) && (id !== 'agents' || !home2)).map(({ id, labelKey, route }) => ({
      id,
      label: t(id === 'chats' && !chatsEnabled ? 'nav.talkToMaestro' : labelKey),
      route: id === 'chats' && !chatsEnabled ? '/?new=1' : route,
      path: `${basePath}${id === 'chats' && !chatsEnabled ? '/?new=1' : route}`,
      isActive: id === current,
      onClick: onClick(id),
    })),
    newChat: { label: t('chats.newChat'), visible: chatsEnabled, disabled: !onboardingResolved, onClick: onNewChat },
  };
}
